/**
 * People section store (Zustand vanilla). Hydrated from the Worker-inlined snapshot before the first render
 * (main.tsx), so the directory and profiles paint with real data. Rows are normalised by id: each table row
 * selects its own person, so a tag change or a selection re-renders that row only.
 */
import { createStore } from "zustand/vanilla";
import { subscribeWithSelector } from "zustand/middleware";
import { useStore } from "zustand";
import type { AdvocacyTop, DirectoryPage, DirectoryQuery, OfficeTop, PeopleSnapshot, PersonProfile, PersonSummary, ResidentTop, TeamPosition, TeamTag, TypeTab } from "./model";
import { toastStore } from "../stores/toastStore";
import { errorText } from "../lib/apiError";

export const PAGE = 8;

export interface PeopleState {
  group: "resident" | "office" | "advocacy";
  at: string;
  query: DirectoryQuery;
  counts: Record<TypeTab, number>;
  total: number;
  areas: string[];
  byId: Record<string, PersonSummary>;
  order: string[];
  loading: boolean;
  selected: string[];                 // compare (2) / bulk select (offices)
  resident: ResidentTop | null;
  office: OfficeTop | null;
  advocacy: AdvocacyTop | null;
  profiles: Record<string, PersonProfile | { error: number }>;

  hydrate: (s: PeopleSnapshot) => void;
  hydrateProfile: (id: string, p: PersonProfile | { error: number }) => void;
  setQuery: (patch: Partial<DirectoryQuery>) => Promise<void>;
  loadMore: () => Promise<void>;
  toggleSelect: (id: string, only2?: boolean) => void;
  clearSelection: () => void;
  loadProfile: (id: string) => Promise<PersonProfile | null>;
  addTag: (id: string, label: string, tone: TeamTag["tone"]) => Promise<void>;
  removeTag: (id: string, tagId: string) => Promise<void>;
  setAdvocacy: (a: AdvocacyTop) => void;
  savePositions: (positions: TeamPosition[]) => Promise<void>;
}

const toParams = (q: DirectoryQuery, offset: number) => {
  const p = new URLSearchParams();
  if (q.type !== "all") p.set("type", q.type);
  if (q.q) p.set("q", q.q);
  p.set("ward", q.ward == null ? "" : String(q.ward));
  if (q.area) p.set("area", q.area);
  if (q.leaning) p.set("leaning", q.leaning);
  p.set("sort", q.sort);
  if (q.office) p.set("office", q.office);
  p.set("offset", String(offset));
  p.set("limit", String(PAGE));
  return p;
};

const api = (path: string, init?: RequestInit) =>
  fetch(`/api/dashboard/people/${path}`, { credentials: "include", headers: init?.body ? { "content-type": "application/json" } : undefined, ...init })
    .then(async (r) => { if (!r.ok) throw new Error(errorText(await r.json().catch(() => null)) || String(r.status)); return r.json(); });

let seq = 0;

export const peopleStore = createStore<PeopleState>()(subscribeWithSelector((set, get) => ({
  group: "office",
  at: "",
  query: { type: "all", q: "", ward: null, area: null, leaning: null, sort: "active" },
  counts: { all: 0, official: 0, resident: 0, lobbyist: 0, org_rep: 0 },
  total: 0,
  areas: [],
  byId: {},
  order: [],
  loading: false,
  selected: [],
  resident: null,
  office: null,
  advocacy: null,
  profiles: {},

  hydrate: (s) => set({
    group: s.group, at: s.at, query: s.query, counts: s.counts, total: s.total, areas: s.areas,
    byId: Object.fromEntries(s.people.map((p) => [p.id, p])), order: s.people.map((p) => p.id),
    resident: s.resident ?? null, office: s.office ?? null, advocacy: s.advocacy ?? null,
  }),
  hydrateProfile: (id, p) => set((st) => ({ profiles: { ...st.profiles, [id]: p } })),

  setQuery: async (patch) => {
    const query = { ...get().query, ...patch };
    const mine = ++seq;
    set({ query, loading: true });
    try {
      const page: DirectoryPage = await api(`directory?${toParams(query, 0)}`);
      if (mine !== seq) return;
      set({ total: page.total, counts: page.counts, byId: { ...get().byId, ...Object.fromEntries(page.people.map((p) => [p.id, p])) }, order: page.people.map((p) => p.id), loading: false });
    } catch {
      if (mine === seq) set({ loading: false });
    }
  },
  loadMore: async () => {
    const { query, order } = get();
    const mine = ++seq;
    set({ loading: true });
    try {
      const page: DirectoryPage = await api(`directory?${toParams(query, order.length)}`);
      if (mine !== seq) return;
      set({ byId: { ...get().byId, ...Object.fromEntries(page.people.map((p) => [p.id, p])) }, order: [...order, ...page.people.map((p) => p.id).filter((id) => !order.includes(id))], loading: false });
    } catch {
      if (mine === seq) set({ loading: false });
    }
  },
  toggleSelect: (id, only2 = false) => set((st) => {
    if (st.selected.includes(id)) return { selected: st.selected.filter((x) => x !== id) };
    const next = [...st.selected, id];
    return { selected: only2 ? next.slice(-2) : next };
  }),
  clearSelection: () => set({ selected: [] }),

  loadProfile: async (id) => {
    const have = get().profiles[id];
    if (have && !("error" in have)) return have;
    try {
      const p: PersonProfile = await api(`${encodeURIComponent(id)}/profile`);
      get().hydrateProfile(id, p);
      return p;
    } catch {
      return null;
    }
  },

  addTag: async (id, label, tone) => {
    const tag: TeamTag = await api(`${encodeURIComponent(id)}/tags`, { method: "POST", body: JSON.stringify({ label, tone }) });
    set((st) => st.byId[id] ? { byId: { ...st.byId, [id]: { ...st.byId[id], tags: [...(st.byId[id].tags ?? []), tag] } } } : {});
  },
  removeTag: async (id, tagId) => {
    const before = get().byId[id];
    set((st) => st.byId[id] ? { byId: { ...st.byId, [id]: { ...st.byId[id], tags: (st.byId[id].tags ?? []).filter((t) => t.id !== tagId) } } } : {});
    try {
      await api(`${encodeURIComponent(id)}/tags/${encodeURIComponent(tagId)}`, { method: "DELETE" });
    } catch {
      if (before) set((st) => ({ byId: { ...st.byId, [id]: before } }));
    }
  },
  setAdvocacy: (a) => set({ advocacy: a }),
  savePositions: async (positions) => {
    const prev = get().advocacy;
    if (prev) set({ advocacy: { ...prev, positions } });
    try {
      const next: AdvocacyTop = await api("positions", { method: "PUT", body: JSON.stringify({ positions }) });
      set({ advocacy: next });
    } catch {
      if (prev) set({ advocacy: prev });
      toastStore.getState().show({ message: "Couldn't save your team's positions" });
    }
  },
})));

export const usePeople = <T,>(selector: (s: PeopleState) => T) => useStore(peopleStore, selector);
export const peopleApi = api;
